'use client'

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface AdminDashboardStats {
  totalUsers: number
  activeUsers: number
  newUsersToday: number
  newUsersThisWeek: number
  totalEnrollments: number
  pendingEnrollments: number
  completedEnrollments: number
  rejectedEnrollments: number
  totalStaff: number
  activeStaff: number
  totalBranches: number
  activeBranches: number
  systemUptime: number
  averageProcessingTime: number
}

export interface AdminUserActivity {
  timestamp: string
  totalLogins: number
  staffLogins: number
  enrollmentOfficerLogins: number
  dataEntryClerkLogins: number
  newRegistrations: number
  enrollmentSubmissions: number
  documentUploads: number
}

export interface AdminRegionalData {
  region: string
  users: number
  enrollments: number
  staff: number
  branches: number
  percentage: number
  growth: number
  status: 'excellent' | 'good' | 'warning' | 'critical'
}

export interface AdminAlerts {
  id: string
  type: 'security' | 'system' | 'user' | 'enrollment'
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  message: string
  timestamp: string
  status: 'active' | 'investigating' | 'resolved'
  affectedUsers?: number
  region?: string
  actionRequired?: boolean
}

export interface AdminUserTrend {
  period: string
  newUsers: number
  activeUsers: number
  deactivatedUsers: number
  pendingVerifications: number
}

export interface SavedView {
  id: string
  name: string
  filters: AdminDashboardFilters
  dateRange: string
  createdAt: string
}

export interface AdminDashboardFilters {
  timeRange: 'today' | '7days' | '30days' | '90days' | 'custom'
  region?: string
  userType?: 'all' | 'staff' | 'citizens'
  status: 'all' | 'active' | 'inactive' | 'pending'
  enrollmentStatus: 'all' | 'completed' | 'pending' | 'rejected'
  customDateRange?: {
    startDate: string
    endDate: string
  }
}

export interface AdminDashboardData {
  stats: AdminDashboardStats
  userActivity: AdminUserActivity[]
  regionalData: AdminRegionalData[]
  alerts: AdminAlerts[]
  userTrends: AdminUserTrend[]
  lastUpdated: string
}

interface AdminDashboardState {
  data: AdminDashboardData | null
  filters: AdminDashboardFilters
  loading: boolean
  isRefreshing: boolean
  error: string | null
  autoRefresh: boolean
  refreshInterval: number // milliseconds
  lastRefreshed: string | null
  selectedView: 'overview' | 'users' | 'enrollments' | 'staff' | 'regional' | 'analytics'

  // UI preferences
  expandedCards: string[]
  savedViews: SavedView[]
  bookmarkedViews: string[]

  // Real-time data
  realTimeEnabled: boolean
  pendingNotifications: number

  // Export/Report preferences
  lastExportDate: string | null
  reportSchedule: 'none' | 'daily' | 'weekly' | 'monthly'
}

const initialState: AdminDashboardState = {
  data: null,
  filters: {
    timeRange: 'today',
    userType: 'all',
    status: 'all',
    enrollmentStatus: 'all',
  },
  loading: false,
  isRefreshing: false,
  error: null,
  autoRefresh: true,
  refreshInterval: 30000, // 30 seconds
  lastRefreshed: null,
  selectedView: 'overview',

  // UI preferences
  expandedCards: ['stats', 'activity'],
  savedViews: [],
  bookmarkedViews: [],

  // Real-time data
  realTimeEnabled: true,
  pendingNotifications: 0,

  // Export/Report preferences
  lastExportDate: null,
  reportSchedule: 'weekly',
}

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',
  initialState,
  reducers: {
    // Data management
    setDashboardData: (state, action: PayloadAction<AdminDashboardData>) => {
      state.data = action.payload
      state.loading = false
      state.isRefreshing = false
      state.error = null
      state.lastRefreshed = new Date().toISOString()
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
      state.isRefreshing = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Filters
    setTimeRange: (state, action: PayloadAction<AdminDashboardFilters['timeRange']>) => {
      state.filters.timeRange = action.payload
      if (action.payload !== 'custom') {
        delete state.filters.customDateRange
      }
    },

    setRegionFilter: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload === undefined) {
        delete state.filters.region
      } else {
        state.filters.region = action.payload
      }
    },

    setUserTypeFilter: (
      state,
      action: PayloadAction<AdminDashboardFilters['userType'] | undefined>
    ) => {
      if (action.payload === undefined) {
        delete state.filters.userType
      } else {
        state.filters.userType = action.payload
      }
    },

    setStatusFilter: (state, action: PayloadAction<AdminDashboardFilters['status']>) => {
      state.filters.status = action.payload
    },

    setEnrollmentStatusFilter: (
      state,
      action: PayloadAction<AdminDashboardFilters['enrollmentStatus']>
    ) => {
      state.filters.enrollmentStatus = action.payload
    },

    setCustomDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
      state.filters.customDateRange = action.payload
      state.filters.timeRange = 'custom'
    },

    clearFilters: (state) => {
      state.filters = {
        timeRange: 'today',
        userType: 'all',
        status: 'all',
        enrollmentStatus: 'all',
      }
    },

    // Auto-refresh
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    // Views
    setSelectedView: (state, action: PayloadAction<AdminDashboardState['selectedView']>) => {
      state.selectedView = action.payload
    },

    // UI preferences
    toggleCardExpansion: (state, action: PayloadAction<string>) => {
      const cardId = action.payload
      const index = state.expandedCards.indexOf(cardId)
      if (index >= 0) {
        state.expandedCards.splice(index, 1)
      } else {
        state.expandedCards.push(cardId)
      }
    },

    setExpandedCards: (state, action: PayloadAction<string[]>) => {
      state.expandedCards = action.payload
    },

    // Saved views
    saveView: (state, action: PayloadAction<Omit<SavedView, 'id' | 'createdAt'>>) => {
      const newView: SavedView = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
      }
      state.savedViews.push(newView)
    },

    deleteView: (state, action: PayloadAction<string>) => {
      state.savedViews = state.savedViews.filter((view) => view.id !== action.payload)
      state.bookmarkedViews = state.bookmarkedViews.filter((id) => id !== action.payload)
    },

    loadView: (state, action: PayloadAction<string>) => {
      const view = state.savedViews.find((v) => v.id === action.payload)
      if (view) {
        state.filters = view.filters
      }
    },

    toggleBookmarkView: (state, action: PayloadAction<string>) => {
      const viewId = action.payload
      const index = state.bookmarkedViews.indexOf(viewId)
      if (index >= 0) {
        state.bookmarkedViews.splice(index, 1)
      } else {
        state.bookmarkedViews.push(viewId)
      }
    },

    // Real-time features
    setRealTimeEnabled: (state, action: PayloadAction<boolean>) => {
      state.realTimeEnabled = action.payload
    },

    setPendingNotifications: (state, action: PayloadAction<number>) => {
      state.pendingNotifications = action.payload
    },

    incrementNotifications: (state) => {
      state.pendingNotifications += 1
    },

    clearNotifications: (state) => {
      state.pendingNotifications = 0
    },

    // Alert management
    updateAlert: (
      state,
      action: PayloadAction<{ alertId: string; status: AdminAlerts['status'] }>
    ) => {
      if (state.data?.alerts) {
        const alert = state.data.alerts.find((a) => a.id === action.payload.alertId)
        if (alert) {
          alert.status = action.payload.status
        }
      }
    },

    dismissAlert: (state, action: PayloadAction<string>) => {
      if (state.data?.alerts) {
        state.data.alerts = state.data.alerts.filter((alert) => alert.id !== action.payload)
      }
    },

    // Export/Report preferences
    setLastExportDate: (state, action: PayloadAction<string>) => {
      state.lastExportDate = action.payload
    },

    setReportSchedule: (state, action: PayloadAction<AdminDashboardState['reportSchedule']>) => {
      state.reportSchedule = action.payload
    },

    // Bulk actions
    applyFiltersFromView: (state, action: PayloadAction<SavedView>) => {
      state.filters = action.payload.filters
    },

    resetDashboard: (state) => {
      return {
        ...initialState,
        savedViews: state.savedViews, // Preserve saved views
        bookmarkedViews: state.bookmarkedViews, // Preserve bookmarks
        reportSchedule: state.reportSchedule, // Preserve report settings
      }
    },
  },
})

export const {
  setDashboardData,
  setLoading,
  setRefreshing,
  setError,
  clearError,
  setTimeRange,
  setRegionFilter,
  setUserTypeFilter,
  setStatusFilter,
  setEnrollmentStatusFilter,
  setCustomDateRange,
  clearFilters,
  setAutoRefresh,
  setRefreshInterval,
  setSelectedView,
  toggleCardExpansion,
  setExpandedCards,
  saveView,
  deleteView,
  loadView,
  toggleBookmarkView,
  setRealTimeEnabled,
  setPendingNotifications,
  incrementNotifications,
  clearNotifications,
  updateAlert,
  dismissAlert,
  setLastExportDate,
  setReportSchedule,
  applyFiltersFromView,
  resetDashboard,
} = adminDashboardSlice.actions

export default adminDashboardSlice.reducer
